import { Card } from "@tellescope/design-system";
import { FileText, Video, BookOpen } from "lucide-react";
import type { ManagedContentRecord } from "@tellescope/types-models";
import { useSecureFileUrl } from "@tellescope/tanstack-query";

const getContentIcon = (type: string | undefined) => {
  switch (type) {
    case "Video":
      return Video;
    case "PDF":
      return FileText;
    case "Article":
    default:
      return BookOpen;
  }
};

export interface ContentCardProps {
  content: ManagedContentRecord;
  onSelect: (content: ManagedContentRecord) => void;
  isLoading: boolean;
  loadingId: string | null;
  headerPhotoStyle: "banner" | "icon";
}

export const ContentCard = ({ content, onSelect, isLoading, loadingId, headerPhotoStyle }: ContentCardProps) => {
  const Icon = getContentIcon(content.type);
  const isThisLoading = isLoading && loadingId === content.id;
  const headerPhotoUrl = useSecureFileUrl(content.headerPhoto);

  const showBanner = headerPhotoStyle === "banner" && !!headerPhotoUrl;
  const showIconPhoto = headerPhotoStyle === "icon" && !!headerPhotoUrl;

  return (
    <Card
      variant="floating"
      padding="root"
      className="cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden"
      onClick={() => !isLoading && onSelect(content)}
    >
      {showBanner && (
        <div className="w-full h-36 overflow-hidden">
          <img src={headerPhotoUrl} alt={content.title} className="w-full h-full object-cover" />
        </div>
      )}
      <Card.Body className="px-4">
        <div className="flex gap-3 items-start">
          <div className="flex-shrink-0">
            {showIconPhoto ? (
              <div className="w-14 h-14 rounded-lg overflow-hidden">
                <img
                  src={headerPhotoUrl}
                  alt={content.title}
                  className={`w-full h-full object-cover ${isThisLoading ? "opacity-50" : ""}`}
                />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon className={`h-5 w-5 text-primary ${isThisLoading ? "animate-pulse" : ""}`} />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate" title={content.title}>
              {content.title}
            </h4>

            {content.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{content.description}</p>
            )}

            {content.category && (
              <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded-full text-xs bg-muted text-muted-foreground">
                {content.category}
              </span>
            )}
          </div>

          <div className="flex-shrink-0">
            <span className="text-xs text-muted-foreground capitalize">{content.type}</span>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
